import { memo, useCallback, useEffect, useRef, useState } from 'react';

import PropTypes from 'prop-types';

import { useGetSettingsQuery } from 'store/api/strapiService/rewardSystemController';

import { CircularProgress } from '@mui/material';

import Button from 'components/UI/Button/Button';
import Checkbox from 'components/UI/Checkbox/Checkbox';
import FormControlLabel from 'components/UI/FormControlLabel/FormControlLabel';
import Loader from 'components/UI/Loader/Loader';
import Typography from 'components/UI/Typography/Typography';
import TermsContent from 'components/pages/RewardSystem/Terms/TermsContent/TermsContent';

import { ReactComponent as Logo } from 'images/Logo/Logo.svg';

import styles from './TermsAcceptation.module.scss';

const TermsAcceptation = ({ onAccept, accepting }) => {
  const [scrolledToBottom, setScrolledToBottom] = useState(false);
  const [agreed, setAgreed] = useState(false);

  const contentRef = useRef(null);

  const { data: settingsData, isLoading } = useGetSettingsQuery();

  const { termsAndConditions } = settingsData?.data ?? {};

  const checkScrolledToBottom = useCallback(() => {
    const contentEl = contentRef.current;
    if (!contentEl) return;

    const noScroll = contentEl.scrollHeight <= contentEl.clientHeight;

    const bottomReached =
      contentEl.scrollHeight - contentEl.scrollTop - contentEl.clientHeight <=
      1;

    if (noScroll || bottomReached) setScrolledToBottom(true);
  }, []);

  const handleAgree = useCallback(
    ({ target: { checked } }) => setAgreed(checked),
    [],
  );

  const removeScrollListener = useCallback(
    () =>
      contentRef.current?.removeEventListener('scroll', checkScrolledToBottom),
    [checkScrolledToBottom],
  );

  useEffect(() => {
    if (termsAndConditions) {
      contentRef.current?.addEventListener('scroll', checkScrolledToBottom);
      checkScrolledToBottom();

      return removeScrollListener;
    }
  }, [termsAndConditions]);

  useEffect(() => {
    scrolledToBottom && removeScrollListener();
  }, [scrolledToBottom]);

  return (
    <div className={styles.wrapper}>
      <div className={styles.dialog}>
        <div className={styles.header}>
          <Logo />
          <Typography type="heading-md" weight="medium" uppercase>
            Read and accept T&C
          </Typography>
          <Typography color="grey">
            Let us help you get to know our product better
          </Typography>
        </div>
        <div ref={contentRef} className={styles.content}>
          {!isLoading ? (
            <TermsContent content={termsAndConditions} />
          ) : (
            <Loader />
          )}
        </div>
        <div className={styles.actions}>
          <div>
            <FormControlLabel
              control={
                <Checkbox
                  checked={agreed}
                  onClick={handleAgree}
                  disabled={!scrolledToBottom}
                />
              }
              label="I have read and agree to the T&C"
            />
            <Typography type="text-xs" color="grey">
              (Please read and scroll to the bottom to accept the Terms and
              Conditions)
            </Typography>
          </div>
          <Button
            startIcon={accepting && <CircularProgress size={24} />}
            onClick={onAccept}
            disabled={!agreed || accepting}
            className={styles.btn}
          >
            Accept
          </Button>
        </div>
      </div>
    </div>
  );
};

TermsAcceptation.propTypes = {
  onAccept: PropTypes.func.isRequired,
  accepting: PropTypes.bool,
};

export default memo(TermsAcceptation);
